import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import parashaMap from '../../parasha_map.json';
import { formatNumberForLocale } from '../utils/hebrewNumbers';

// Build a map from English parasha name to Hebrew name
const parashaNameMap: Record<string, string> = (
  parashaMap as { english: string; hebrew: string }[]
).reduce(
  (acc, p) => {
    acc[p.english] = p.hebrew;
    return acc;
  },
  {} as Record<string, string>,
);

// AI comparison result (single explanation + total score 1-10)
interface ComparisonData {
  success: boolean;
  comparison: {
    explanation?: string;
    total_score?: number;
  };
}

interface LocationState {
  cachedData?: ComparisonData;
  parasha?: string;
  verseIndex?: number;
}

const CompareResultsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [loading, setLoading] = useState(!locationState?.cachedData);
  const [error, setError] = useState<string | null>(null);
  const [data] = useState<ComparisonData | null>(locationState?.cachedData || null);

  useEffect(() => {
    if (locationState?.cachedData) {
      setLoading(false);
      return;
    }

    // No cached data available
    if (!locationState?.cachedData) {
      setError(t('no_comparison_data') || 'No comparison data available');
      setLoading(false);
    }
  }, [locationState, t]);

  // Overall score: total_score is 1-10, convert to percentage
  const totalScoreRaw = data?.comparison?.total_score ?? 0;
  const overallScore = typeof totalScoreRaw === 'number' ? Math.round(totalScoreRaw * 10) : 0;

  // Get color based on score (0-10 scale)
  const getScoreColor = (score: number): string => {
    if (score >= 8) return '#16a34a'; // green
    if (score >= 6) return '#ca8a04'; // yellow
    if (score >= 4) return '#ea580c'; // orange
    return '#dc2626'; // red
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-[#faf9f6]" dir={isRtl ? 'rtl' : 'ltr'}>
        <TopBar title={t('analyzing') || 'Analyzing...'} onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#caa42e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#132a49] font-medium">
              {t('analyzing_audio') || 'Analyzing your reading...'}
            </p>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-screen bg-[#faf9f6]" dir={isRtl ? 'rtl' : 'ltr'}>
        <TopBar title={t('error') || 'Error'} onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-red-50 text-red-700 p-6 rounded-2xl text-center max-w-md">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="font-bold text-lg mb-2">
              {t('comparison_failed') || 'Comparison Failed'}
            </h2>
            <p className="text-sm mb-4">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              {t('go_back') || 'Go Back'}
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#faf9f6]" dir={isRtl ? 'rtl' : 'ltr'}>
      <TopBar title={t('comparison_results') || 'Comparison Results'} onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto px-4 py-6" style={{ paddingBottom: 100 }}>
        {/* Overall Score Card */}
        <div className="bg-gradient-to-r from-[#132a49] to-[#1e4a6e] rounded-2xl shadow-lg p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="text-start">
              {locationState?.parasha && (
                <h2 className="text-xl font-bold mb-1">
                  {isRtl
                    ? parashaNameMap[locationState.parasha] || locationState.parasha
                    : locationState.parasha}
                </h2>
              )}
              <p className="text-sm opacity-80">
                {t('ai_analysis') || 'AI Word Analysis'}
                {locationState?.verseIndex !== undefined &&
                  ` - ${t('verse') || 'Verse'} ${formatNumberForLocale(locationState.verseIndex + 1, i18n.language || '')}`}
              </p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold" style={{ color: getScoreColor(totalScoreRaw) }}>
                {overallScore}%
              </div>
              <div className="text-xs opacity-80 mt-1">{t('overall_score') || 'Overall Score'}</div>
            </div>
          </div>
        </div>

        {/* AI explanation */}
        {data?.comparison?.explanation && (
          <div className="bg-white rounded-2xl shadow-md p-4 mb-6">
            <h2 className="text-lg font-bold text-[#132a49] p-0 m-0 mb-2">
              {t('ai_feedback') || 'AI Feedback'}
            </h2>
            <div className="text-base text-gray-700 leading-relaxed" dir="rtl">
              {data.comparison.explanation}
            </div>
          </div>
        )}

        {/* Tips Card */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-200 mb-6">
          <h3 className="font-semibold text-[#132a49] mb-3 flex items-center gap-2">
            💡 {t('improvement_tips') || 'Tips for Improvement'}
          </h3>
          <ul className="text-sm text-gray-700 space-y-2">
            {overallScore < 60 && (
              <li>
                •{' '}
                {t('tip_practice_slowly') ||
                  'Practice each word slowly, focusing on the vowel sounds'}
              </li>
            )}
            {overallScore >= 60 && overallScore < 80 && (
              <li>
                •{' '}
                {t('tip_taam_focus') ||
                  'Listen to the melody pattern and syllable stress (milra/milel)'}
              </li>
            )}
            {overallScore >= 80 && (
              <li>
                • {t('tip_excellent') || 'Excellent work! Keep practicing to maintain your skills'}
              </li>
            )}
          </ul>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/practice')}
          className="w-full py-4 bg-[#132a49] text-white rounded-xl font-semibold hover:bg-[#0f2239] transition-colors"
        >
          {isRtl ? '→' : '←'} {t('back_to_practice') || 'Back to Practice'}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default CompareResultsPage;
